import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = '/api/ai-queue';

interface Queue { id: number; queue_name: string; display_name: string; is_active: boolean; }
interface KB    { id: string; name: string; description?: string; }
interface Profile {
  id: string; tenant_id: number; queue_id: number; queue_name: string;
  knowledge_base_id?: string; kb_name?: string; name: string;
  system_prompt: string; greeting_script?: string; compliance_notice?: string;
  escalation_prompt?: string; closing_script?: string;
  ai_model: string; temperature: number; max_tokens: number;
  is_active: boolean; is_published: boolean; created_at: string;
}
interface Checklist { id: string; type: 'opening'|'closing'; label: string; hint?: string; order_index: number; is_required: boolean; }
interface QuickResponse { id: string; label: string; response_text: string; shortcut?: string; order_index: number; }
interface Action { id: string; label: string; action_type: string; action_url?: string; icon?: string; open_in: string; order_index: number; }
interface Summary { id: string; call_id?: string; caller_number?: string; summary?: string; customer_issue?: string; resolution?: string; sentiment?: string; created_at: string; agent_username?: string; queue_id?: number; }

const CARD: React.CSSProperties = {
  background: '#ffffff', border: '1px solid #e2e8f0',
  borderRadius: 10, padding: 18,
};
const INPUT: React.CSSProperties = {
  width: '100%', background: '#f8fafc', border: '1px solid #cbd5e1',
  borderRadius: 6, padding: '7px 10px', color: '#1e293b', fontSize: 13,
  boxSizing: 'border-box',
};
const TEXTAREA: React.CSSProperties = { ...INPUT, minHeight: 90, resize: 'vertical' as const, fontFamily: 'inherit' };
const BTN_PRI: React.CSSProperties = {
  background: 'linear-gradient(135deg,#3b82f6,#6366f1)', color: '#fff', border: 'none',
  borderRadius: 6, padding: '7px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
};
const BTN_SEC: React.CSSProperties = {
  background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0',
  borderRadius: 6, padding: '7px 14px', cursor: 'pointer', fontSize: 13,
};
const BTN_DANGER: React.CSSProperties = {
  background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca',
  borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12,
};
const LABEL: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' as const, letterSpacing: '0.05em' };
const SENT_COLOR: Record<string, string> = { positive:'#10b981', negative:'#ef4444', neutral:'#94a3b8', mixed:'#f59e0b' };

type Tab = 'profiles' | 'checklists' | 'responses' | 'actions' | 'summaries';

export default function QueueAIManagement({ user }: { user: any }) {
  const auth = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
  const [tab, setTab] = useState<Tab>('profiles');
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [queues, setQueues] = useState<Queue[]>([]);
  const [kbs, setKBs] = useState<KB[]>([]);
  const [selected, setSelected] = useState<Profile | null>(null);
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [responses, setResponses] = useState<QuickResponse[]>([]);
  const [actions, setActions] = useState<Action[]>([]);
  const [summaries, setSummaries] = useState<Summary[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCreateProfile, setShowCreateProfile] = useState(false);
  const [form, setForm] = useState<Partial<Profile>>({});
  const [stats, setStats] = useState<any>({});

  // Load top-level data
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, qRes, kbRes, stRes] = await Promise.all([
        axios.get(`${API}/profiles`, auth),
        axios.get('/api/queues', auth),
        axios.get(`${API}/knowledge-bases`, auth).catch(() => ({ data: [] })),
        axios.get(`${API}/stats`, auth).catch(() => ({ data: {} })),
      ]);
      setProfiles(pRes.data || []);
      setQueues(qRes.data || []);
      setKBs(kbRes.data || []);
      setStats(stRes.data || {});
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadProfileDetail = useCallback(async (p: Profile) => {
    setSelected(p);
    setForm({ ...p });
    const [clRes, qrRes, acRes] = await Promise.all([
      axios.get(`${API}/profiles/${p.id}/checklists`, auth),
      axios.get(`${API}/profiles/${p.id}/quick-responses`, auth),
      axios.get(`${API}/profiles/${p.id}/actions`, auth),
    ]);
    setChecklists(clRes.data || []);
    setResponses(qrRes.data || []);
    setActions(acRes.data || []);
    setTab('checklists');
  }, []);

  const loadSummaries = useCallback(async () => {
    const r = await axios.get(`${API}/summaries?limit=50`, auth);
    setSummaries(r.data || []);
  }, []);

  useEffect(() => { if (tab === 'summaries') loadSummaries(); }, [tab, loadSummaries]);

  // Profile CRUD
  const createProfile = async () => {
    if (!form.queue_id) return alert('Please select a queue');
    setSaving(true);
    try {
      const q = queues.find(q => q.id === Number(form.queue_id));
      await axios.post(`${API}/profiles`, { ...form, queue_name: q?.display_name || q?.queue_name }, auth);
      setShowCreateProfile(false);
      setForm({});
      loadAll();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Error creating profile');
    } finally { setSaving(false); }
  };

  const saveProfile = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const r = await axios.put(`${API}/profiles/${selected.id}`, form, auth);
      setSelected(r.data);
      setForm(r.data);
      loadAll();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Error saving');
    } finally { setSaving(false); }
  };

  const deleteProfile = async (id: string) => {
    if (!window.confirm('Delete this AI queue profile and all its checklists/responses/actions?')) return;
    await axios.delete(`${API}/profiles/${id}`, auth);
    if (selected?.id === id) { setSelected(null); setForm({}); }
    loadAll();
  };

  const togglePublish = async () => {
    if (!selected) return;
    const r = await axios.put(`${API}/profiles/${selected.id}`, { is_published: !selected.is_published }, auth);
    setSelected(r.data); setForm(r.data); loadAll();
  };

  // Checklist CRUD
  const [clForm, setClForm] = useState({ type: 'opening', label: '', hint: '', is_required: false });
  const addChecklist = async () => {
    if (!selected || !clForm.label.trim()) return;
    await axios.post(`${API}/profiles/${selected.id}/checklists`, clForm, auth);
    setClForm({ type: 'opening', label: '', hint: '', is_required: false });
    const r = await axios.get(`${API}/profiles/${selected.id}/checklists`, auth);
    setChecklists(r.data || []);
  };
  const deleteChecklist = async (id: string) => {
    await axios.delete(`${API}/checklists/${id}`, auth);
    setChecklists(cl => cl.filter(c => c.id !== id));
  };

  // Quick response CRUD
  const [qrForm, setQrForm] = useState({ label: '', response_text: '', shortcut: '' });
  const addQR = async () => {
    if (!selected || !qrForm.label.trim() || !qrForm.response_text.trim()) return;
    await axios.post(`${API}/profiles/${selected.id}/quick-responses`, qrForm, auth);
    setQrForm({ label: '', response_text: '', shortcut: '' });
    const r = await axios.get(`${API}/profiles/${selected.id}/quick-responses`, auth);
    setResponses(r.data || []);
  };
  const deleteQR = async (id: string) => {
    await axios.delete(`${API}/quick-responses/${id}`, auth);
    setResponses(qs => qs.filter(q => q.id !== id));
  };

  // Action CRUD
  const [acForm, setAcForm] = useState({ label: '', action_type: 'url', action_url: '', open_in: 'tab' });
  const addAction = async () => {
    if (!selected || !acForm.label.trim()) return;
    await axios.post(`${API}/profiles/${selected.id}/actions`, acForm, auth);
    setAcForm({ label: '', action_type: 'url', action_url: '', open_in: 'tab' });
    const r = await axios.get(`${API}/profiles/${selected.id}/actions`, auth);
    setActions(r.data || []);
  };
  const deleteAction = async (id: string) => {
    await axios.delete(`${API}/actions/${id}`, auth);
    setActions(as => as.filter(a => a.id !== id));
  };

  const TABS: { id: Tab; label: string }[] = [
    { id:'profiles',   label:'Profiles' },
    { id:'checklists', label:'Checklists' },
    { id:'responses',  label:'Quick Responses' },
    { id:'actions',    label:'Actions' },
    { id:'summaries',  label:'Call Summaries' },
  ];

  const profilesWithoutConfig = queues.filter(q => !profiles.find(p => p.queue_id === q.id));

  return (
    <div style={{ padding: 16, height: '100%', overflowY: 'auto', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16, flexWrap:'wrap', gap:8 }}>
        <div>
          <h2 style={{ margin:0, fontSize:18, fontWeight:700, color:'#1e293b' }}>AI Queue Assistant</h2>
          <p style={{ margin:'2px 0 0', fontSize:12, color:'#64748b' }}>Configure AI assistance per queue — knowledge base, scripts, checklists and quick responses</p>
        </div>
        <button style={BTN_PRI} onClick={() => { setShowCreateProfile(true); setForm({}); }}>+ New Profile</button>
      </div>

      {/* Stats row */}
      {stats.profiles && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:10, marginBottom:16 }}>
          {[
            { label:'Active Profiles', val: stats.profiles?.active || 0, color:'#3b82f6' },
            { label:'Sessions (7d)',   val: stats.sessions?.week || 0, color:'#10b981' },
            { label:'Total Sessions',  val: stats.sessions?.total || 0, color:'#8b5cf6' },
            { label:'Summaries (30d)', val: stats.summaries?.total || 0, color:'#f59e0b' },
          ].map(s => (
            <div key={s.label} style={{ ...CARD, padding:12, textAlign:'center' }}>
              <div style={{ fontSize:22, fontWeight:800, color:s.color }}>{s.val}</div>
              <div style={{ fontSize:11, color:'#64748b', marginTop:2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, marginBottom:14, borderBottom:'1px solid #e2e8f0', paddingBottom:1 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            background:'none', border:'none', cursor:'pointer', padding:'7px 12px', fontSize:13, fontWeight:600,
            color: tab===t.id ? '#3b82f6' : '#64748b',
            borderBottom: tab===t.id ? '2px solid #3b82f6' : '2px solid transparent',
            marginBottom:-1,
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── PROFILES TAB ──────────────────────────────────────────────────── */}
      {tab === 'profiles' && (
        <div>
          {loading ? (
            <p style={{ color:'#64748b', textAlign:'center' }}>Loading...</p>
          ) : profiles.length === 0 ? (
            <div style={{ textAlign:'center', padding:40, color:'#64748b' }}>
              <div style={{ fontSize:40, marginBottom:8 }}>🤖</div>
              <p>No AI queue profiles yet. Create one to get started.</p>
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:12 }}>
              {profiles.map(p => (
                <div key={p.id} style={{ ...CARD, cursor:'pointer', border: selected?.id===p.id ? '1px solid #3b82f6' : '1px solid #e2e8f0' }}
                  onClick={() => loadProfileDetail(p)}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                    <div>
                      <div style={{ fontWeight:700, fontSize:14, color:'#1e293b' }}>{p.name}</div>
                      <div style={{ fontSize:12, color:'#64748b' }}>Queue: {p.queue_name || `#${p.queue_id}`}</div>
                    </div>
                    <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                      <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:20,
                        background: p.is_published ? '#d1fae5' : '#f1f5f9',
                        color: p.is_published ? '#10b981' : '#64748b' }}>
                        {p.is_published ? 'LIVE' : 'DRAFT'}
                      </span>
                      <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:20,
                        background: p.is_active ? '#dbeafe' : '#fee2e2',
                        color: p.is_active ? '#3b82f6' : '#ef4444' }}>
                        {p.is_active ? 'ON' : 'OFF'}
                      </span>
                    </div>
                  </div>
                  <div style={{ fontSize:11, color:'#64748b', marginBottom:8, lineHeight:1.4 }}>
                    {p.system_prompt?.slice(0, 90)}...
                  </div>
                  {p.kb_name && (
                    <div style={{ fontSize:11, color:'#60a5fa', marginBottom:6 }}>📚 {p.kb_name}</div>
                  )}
                  <div style={{ display:'flex', gap:6, justifyContent:'flex-end', marginTop:8 }}>
                    <button style={BTN_SEC} onClick={e => { e.stopPropagation(); loadProfileDetail(p); }}>Edit</button>
                    <button style={BTN_DANGER} onClick={e => { e.stopPropagation(); deleteProfile(p.id); }}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {profilesWithoutConfig.length > 0 && (
            <div style={{ marginTop:16, padding:12, background:'#fffbeb', border:'1px solid #fde68a', borderRadius:8 }}>
              <div style={{ fontSize:12, color:'#f59e0b', fontWeight:600, marginBottom:6 }}>Queues without AI profiles:</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {profilesWithoutConfig.map(q => (
                  <span key={q.id} style={{ fontSize:11, padding:'3px 8px', background:'#fef3c7', borderRadius:12, color:'#d97706', cursor:'pointer' }}
                    onClick={() => { setForm({ queue_id: q.id, queue_name: q.display_name, name: `${q.display_name} AI` }); setShowCreateProfile(true); }}>
                    {q.display_name || q.queue_name} →
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── CHECKLISTS TAB ────────────────────────────────────────────────── */}
      {tab === 'checklists' && (
        <div>
          {!selected ? (
            <p style={{ color:'#64748b', textAlign:'center', padding:40 }}>← Select a profile from the Profiles tab to manage its checklists</p>
          ) : (
            <>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14 }}>
                <span style={{ fontSize:13, fontWeight:600, color:'#1e293b' }}>Profile: {selected.name}</span>
                <button style={BTN_SEC} onClick={saveProfile} disabled={saving}>{saving ? 'Saving...' : 'Save Profile'}</button>
                <button style={{ ...BTN_PRI, background: selected.is_published ? 'rgba(239,68,68,.6)' : undefined }}
                  onClick={togglePublish}>{selected.is_published ? 'Unpublish' : 'Publish'}</button>
              </div>

              {/* Profile form */}
              <div style={{ ...CARD, marginBottom:14 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
                  <div>
                    <label style={LABEL}>Profile Name</label>
                    <input style={INPUT} value={form.name||''} onChange={e => setForm(f => ({...f,name:e.target.value}))} />
                  </div>
                  <div>
                    <label style={LABEL}>Knowledge Base</label>
                    <select style={INPUT} value={form.knowledge_base_id||''} onChange={e => setForm(f => ({...f,knowledge_base_id:e.target.value||undefined}))}>
                      <option value="">-- None (no RAG) --</option>
                      {kbs.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={LABEL}>AI Model</label>
                    <select style={INPUT} value={form.ai_model||'gpt-4o'} onChange={e => setForm(f => ({...f,ai_model:e.target.value}))}>
                      <option value="gpt-4o">GPT-4o</option>
                      <option value="gpt-4o-mini">GPT-4o mini</option>
                      <option value="gpt-4-turbo">GPT-4 Turbo</option>
                    </select>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                    <div>
                      <label style={LABEL}>Temperature</label>
                      <input type="number" style={INPUT} min="0" max="2" step="0.1" value={form.temperature??0.4} onChange={e => setForm(f => ({...f,temperature:parseFloat(e.target.value)}))} />
                    </div>
                    <div>
                      <label style={LABEL}>Max Tokens</label>
                      <input type="number" style={INPUT} min="64" max="4096" step="64" value={form.max_tokens??512} onChange={e => setForm(f => ({...f,max_tokens:parseInt(e.target.value)}))} />
                    </div>
                  </div>
                </div>
                <div style={{ marginBottom:10 }}>
                  <label style={LABEL}>System Prompt (AI behavior instructions)</label>
                  <textarea style={TEXTAREA} value={form.system_prompt||''} onChange={e => setForm(f => ({...f,system_prompt:e.target.value}))} />
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <div>
                    <label style={LABEL}>Greeting Script</label>
                    <textarea style={{...TEXTAREA, minHeight:60}} value={form.greeting_script||''} onChange={e => setForm(f => ({...f,greeting_script:e.target.value}))} placeholder="Script for agents to open calls..." />
                  </div>
                  <div>
                    <label style={LABEL}>Compliance Notice</label>
                    <textarea style={{...TEXTAREA, minHeight:60}} value={form.compliance_notice||''} onChange={e => setForm(f => ({...f,compliance_notice:e.target.value}))} placeholder="Required disclosures..." />
                  </div>
                  <div>
                    <label style={LABEL}>Escalation Prompt</label>
                    <textarea style={{...TEXTAREA, minHeight:60}} value={form.escalation_prompt||''} onChange={e => setForm(f => ({...f,escalation_prompt:e.target.value}))} placeholder="What to say when escalating..." />
                  </div>
                  <div>
                    <label style={LABEL}>Closing Script</label>
                    <textarea style={{...TEXTAREA, minHeight:60}} value={form.closing_script||''} onChange={e => setForm(f => ({...f,closing_script:e.target.value}))} placeholder="Script for wrapping up calls..." />
                  </div>
                </div>
              </div>

              {/* Checklists */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                {(['opening','closing'] as const).map(type => (
                  <div key={type} style={CARD}>
                    <h4 style={{ margin:'0 0 10px', fontSize:13, fontWeight:700, textTransform:'capitalize', color:'#1e293b' }}>
                      {type === 'opening' ? '▶️' : '🏁'} {type} Checklist
                    </h4>
                    {checklists.filter(c => c.type === type).map(c => (
                      <div key={c.id} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, padding:'6px 8px', background:'#f8fafc', borderRadius:6, border:'1px solid #e2e8f0' }}>
                        <span style={{ flex:1, fontSize:12, color:'#1e293b' }}>{c.label}{c.is_required && <span style={{ color:'#ef4444', marginLeft:4 }}>*</span>}</span>
                        <button style={BTN_DANGER} onClick={() => deleteChecklist(c.id)}>✕</button>
                      </div>
                    ))}
                    <div style={{ display:'flex', gap:6, marginTop:8 }}>
                      <input style={{...INPUT, flex:1}} placeholder={`Add ${type} step...`}
                        value={clForm.type===type ? clForm.label : ''}
                        onChange={e => setClForm({ type, label:e.target.value, hint:'', is_required:false })} />
                      <button style={BTN_PRI} onClick={addChecklist}>+</button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── QUICK RESPONSES TAB ───────────────────────────────────────────── */}
      {tab === 'responses' && (
        <div>
          {!selected ? (
            <p style={{ color:'#64748b', textAlign:'center', padding:40 }}>← Select a profile first</p>
          ) : (
            <>
              <p style={{ fontSize:12, color:'#64748b', marginBottom:12 }}>Pre-built responses agents can paste with one click. Profile: <strong>{selected.name}</strong></p>
              <div style={{ ...CARD, marginBottom:14 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
                  <div>
                    <label style={LABEL}>Label</label>
                    <input style={INPUT} placeholder="e.g. Account Verification" value={qrForm.label} onChange={e => setQrForm(f => ({...f,label:e.target.value}))} />
                  </div>
                  <div>
                    <label style={LABEL}>Shortcut (optional)</label>
                    <input style={INPUT} placeholder="e.g. /verify" value={qrForm.shortcut} onChange={e => setQrForm(f => ({...f,shortcut:e.target.value}))} />
                  </div>
                </div>
                <div style={{ marginBottom:10 }}>
                  <label style={LABEL}>Response Text</label>
                  <textarea style={TEXTAREA} placeholder="The full response text to paste..." value={qrForm.response_text} onChange={e => setQrForm(f => ({...f,response_text:e.target.value}))} />
                </div>
                <button style={BTN_PRI} onClick={addQR}>Add Quick Response</button>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:10 }}>
                {responses.map(r => (
                  <div key={r.id} style={{ ...CARD, padding:12 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                      <span style={{ fontWeight:700, fontSize:13, color:'#1e293b' }}>{r.label}</span>
                      {r.shortcut && <code style={{ fontSize:11, color:'#60a5fa' }}>{r.shortcut}</code>}
                    </div>
                    <p style={{ fontSize:12, color:'#64748b', margin:'0 0 8px', lineHeight:1.4 }}>{r.response_text.slice(0,120)}...</p>
                    <button style={BTN_DANGER} onClick={() => deleteQR(r.id)}>Delete</button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── ACTIONS TAB ───────────────────────────────────────────────────── */}
      {tab === 'actions' && (
        <div>
          {!selected ? (
            <p style={{ color:'#64748b', textAlign:'center', padding:40 }}>← Select a profile first</p>
          ) : (
            <>
              <p style={{ fontSize:12, color:'#64748b', marginBottom:12 }}>One-click buttons shown during calls. Profile: <strong>{selected.name}</strong></p>
              <div style={{ ...CARD, marginBottom:14 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:10 }}>
                  <div>
                    <label style={LABEL}>Label</label>
                    <input style={INPUT} placeholder="Open CRM" value={acForm.label} onChange={e => setAcForm(f => ({...f,label:e.target.value}))} />
                  </div>
                  <div>
                    <label style={LABEL}>Type</label>
                    <select style={INPUT} value={acForm.action_type} onChange={e => setAcForm(f => ({...f,action_type:e.target.value}))}>
                      <option value="url">URL Link</option>
                      <option value="copy">Copy Text</option>
                      <option value="crm">CRM Lookup</option>
                      <option value="internal">Internal Page</option>
                    </select>
                  </div>
                  <div>
                    <label style={LABEL}>Open In</label>
                    <select style={INPUT} value={acForm.open_in} onChange={e => setAcForm(f => ({...f,open_in:e.target.value}))}>
                      <option value="tab">New Tab</option>
                      <option value="popup">Popup</option>
                      <option value="embed">Embed</option>
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom:10 }}>
                  <label style={LABEL}>URL / Data</label>
                  <input style={INPUT} placeholder="https://..." value={acForm.action_url} onChange={e => setAcForm(f => ({...f,action_url:e.target.value}))} />
                </div>
                <button style={BTN_PRI} onClick={addAction}>Add Action</button>
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:10 }}>
                {actions.map(a => (
                  <div key={a.id} style={{ ...CARD, padding:10, minWidth:180, display:'flex', alignItems:'center', gap:8 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:13, color:'#1e293b' }}>{a.label}</div>
                      <div style={{ fontSize:11, color:'#64748b' }}>{a.action_type} · {a.open_in}</div>
                    </div>
                    <button style={BTN_DANGER} onClick={() => deleteAction(a.id)}>✕</button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── SUMMARIES TAB ─────────────────────────────────────────────────── */}
      {tab === 'summaries' && (
        <div>
          <p style={{ fontSize:12, color:'#64748b', marginBottom:12 }}>AI-generated post-call summaries from all queue sessions.</p>
          {summaries.length === 0 ? (
            <p style={{ color:'#64748b', textAlign:'center', padding:40 }}>No summaries yet. Complete AI-assisted calls to generate summaries.</p>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {summaries.map(s => (
                <div key={s.id} style={CARD}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                    <div>
                      <span style={{ fontWeight:700, color:'#1e293b', fontSize:13 }}>{s.caller_number || 'Unknown caller'}</span>
                      {s.agent_username && <span style={{ fontSize:11, color:'#64748b', marginLeft:8 }}>Agent: {s.agent_username}</span>}
                    </div>
                    <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                      {s.sentiment && <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:12, background:'#f1f5f9', color: SENT_COLOR[s.sentiment] }}>{s.sentiment}</span>}
                      <span style={{ fontSize:11, color:'#64748b' }}>{new Date(s.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  {s.customer_issue && <p style={{ fontSize:12, color:'#94a3b8', margin:'0 0 4px' }}><strong>Issue:</strong> {s.customer_issue}</p>}
                  {s.summary && <p style={{ fontSize:12, color:'#1e293b', margin:'0 0 4px', lineHeight:1.5 }}>{s.summary}</p>}
                  {s.resolution && <p style={{ fontSize:12, color:'#10b981', margin:0 }}><strong>Resolution:</strong> {s.resolution}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── CREATE PROFILE MODAL ──────────────────────────────────────────── */}
      {showCreateProfile && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}>
          <div style={{ ...CARD, width:460, maxHeight:'90vh', overflowY:'auto' }}>
            <h3 style={{ margin:'0 0 16px', fontSize:16 }}>New AI Queue Profile</h3>
            <div style={{ marginBottom:10 }}>
              <label style={LABEL}>Queue</label>
              <select style={INPUT} value={form.queue_id||''} onChange={e => {
                const q = queues.find(q => q.id === Number(e.target.value));
                setForm(f => ({...f, queue_id:Number(e.target.value), queue_name:q?.display_name||q?.queue_name, name:`${q?.display_name||q?.queue_name} AI`}));
              }}>
                <option value="">-- Select Queue --</option>
                {queues.filter(q => !profiles.find(p => p.queue_id === q.id)).map(q => (
                  <option key={q.id} value={q.id}>{q.display_name || q.queue_name}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom:10 }}>
              <label style={LABEL}>Profile Name</label>
              <input style={INPUT} value={form.name||''} onChange={e => setForm(f => ({...f,name:e.target.value}))} />
            </div>
            <div style={{ marginBottom:10 }}>
              <label style={LABEL}>Knowledge Base</label>
              <select style={INPUT} value={form.knowledge_base_id||''} onChange={e => setForm(f => ({...f,knowledge_base_id:e.target.value||undefined}))}>
                <option value="">-- None (general AI) --</option>
                {kbs.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
              </select>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={LABEL}>System Prompt</label>
              <textarea style={TEXTAREA} value={form.system_prompt||'You are a helpful call center assistant. Help the agent resolve customer issues efficiently.'} onChange={e => setForm(f => ({...f,system_prompt:e.target.value}))} />
            </div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button style={BTN_SEC} onClick={() => { setShowCreateProfile(false); setForm({}); }}>Cancel</button>
              <button style={BTN_PRI} onClick={createProfile} disabled={saving}>{saving ? 'Creating...' : 'Create Profile'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
